import { Sun, Utensils, Tent, Mountain, TreePine, Gamepad2, Flame, Moon, Backpack, Droplets } from 'lucide-react'
import { useLocale } from '../i18n/LocaleContext'
import { useSchedule } from '../hooks/useSchedule'
import SectionHeading from './ui/SectionHeading'
import type { ScheduleItem } from '../types/wp'

const iconMap: Record<string, typeof Sun> = {
  sun: Sun,
  utensils: Utensils,
  tent: Tent,
  mountain: Mountain,
  trees: TreePine,
  gamepad: Gamepad2,
  campfire: Flame,
  moon: Moon,
  backpack: Backpack,
  droplets: Droplets,
}

// Fallback schedule shown when WordPress is not connected
const fallbackSchedule: ScheduleItem[] = [
  { id: 1, timeStart: '07:00', timeEnd: '07:30', activity: { hu: 'Ébresztő és reggeli torna', ro: 'Trezirea și gimnastică de dimineață' }, icon: 'sun' },
  { id: 2, timeStart: '07:30', timeEnd: '08:30', activity: { hu: 'Reggeli', ro: 'Mic dejun' }, icon: 'utensils' },
  { id: 3, timeStart: '08:30', timeEnd: '12:30', activity: { hu: 'Délelőtti programok (túra, mászófal, kézműves)', ro: 'Programe de dimineață (excursie, escaladă, ateliere)' }, icon: 'mountain' },
  { id: 4, timeStart: '12:30', timeEnd: '13:30', activity: { hu: 'Ebéd', ro: 'Prânz' }, icon: 'utensils' },
  { id: 5, timeStart: '13:30', timeEnd: '15:00', activity: { hu: 'Pihenő / szabad program', ro: 'Odihnă / program liber' }, icon: 'tent' },
  { id: 6, timeStart: '15:00', timeEnd: '15:30', activity: { hu: 'Uzsonna', ro: 'Gustare' }, icon: 'utensils' },
  { id: 7, timeStart: '15:30', timeEnd: '18:30', activity: { hu: 'Délutáni programok (csapatjátékok, patakparti kalandok)', ro: 'Programe de după-amiază (jocuri de echipă, aventuri la pârâu)' }, icon: 'gamepad' },
  { id: 8, timeStart: '18:30', timeEnd: '19:30', activity: { hu: 'Vacsora', ro: 'Cină' }, icon: 'utensils' },
  { id: 9, timeStart: '19:30', timeEnd: '21:00', activity: { hu: 'Esti program (tábortűz, kürtőskalács, csillaglesés)', ro: 'Program de seară (foc de tabără, kürtőskalács, observarea stelelor)' }, icon: 'campfire' },
  { id: 10, timeStart: '21:00', timeEnd: '21:30', activity: { hu: 'Takarodó', ro: 'Stingerea' }, icon: 'moon' },
]

export default function DailySchedule() {
  const { locale, t } = useLocale()
  const { schedule, loading } = useSchedule()

  const items = schedule.length > 0 ? schedule : fallbackSchedule

  if (loading) return null

  return (
    <section className="section-padding bg-surface">
      <div className="container-max">
        <SectionHeading title={t.schedule.title} subtitle={t.schedule.subtitle} />

        <div className="relative max-w-2xl mx-auto">
          {/* Vertical line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-primary/20 -translate-x-1/2" />

          {items.map((item, i) => {
            const Icon = iconMap[item.icon] || Sun
            const isLeft = i % 2 === 0

            return (
              <div key={item.id} className="relative flex items-start mb-8 last:mb-0">
                {/* Desktop: alternating layout */}
                <div className={`hidden md:flex w-full items-center gap-4 ${isLeft ? '' : 'flex-row-reverse'}`}>
                  <div className={`flex-1 ${isLeft ? 'text-right' : 'text-left'}`}>
                    <div className={`inline-block bg-white rounded-card p-4 shadow-subtle ${isLeft ? 'mr-4' : 'ml-4'}`}>
                      <p className="text-xs font-medium text-primary mb-1">
                        {item.timeStart}–{item.timeEnd}
                      </p>
                      <p className="text-sm font-medium text-secondary">{item.activity[locale]}</p>
                    </div>
                  </div>

                  {/* Center dot */}
                  <div className="relative z-10 flex-shrink-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-subtle">
                    <Icon size={18} className="text-white" strokeWidth={2} />
                  </div>

                  <div className="flex-1" />
                </div>

                {/* Mobile: single column */}
                <div className="flex md:hidden items-start gap-4">
                  <div className="relative z-10 flex-shrink-0 w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-subtle">
                    <Icon size={18} className="text-white" strokeWidth={2} />
                  </div>
                  <div className="bg-white rounded-card p-4 shadow-subtle flex-1">
                    <p className="text-xs font-medium text-primary mb-1">
                      {item.timeStart}–{item.timeEnd}
                    </p>
                    <p className="text-sm font-medium text-secondary">{item.activity[locale]}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
